import * as z from "zod";
import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  CalendarStatesRefused,
  CalendarSubscriptionNotFound,
  ErrorCode,
  EventNotFound,
  TableClaimRefused,
  TableKindNotFound,
  TableOrderNotFound,
  UnexpectedApiError,
  ValidationError,
} from "../errors";
import { parse } from "../model/common";
import { PublicEventDetail, PublicEventList, type PublicEventQuery } from "../model/public-event";
import { ShowCalendarSubscription } from "../model/show-calendar";
import { PublicTableShow, ReservedTable, TableReservation } from "../model/vendor-show";
import { fail, forward, type Result } from "../result";
import type { Transport } from "../transport";

const StatesEnvelope = z.object({ states: z.array(z.string()) });

/** The characters an id is made of. */
const EVENT_ID = /^[\w-]+$/;

/**
 * `/events`: the card shows anyone may read, the calendars' listings and the
 * shows shops host. Unauthenticated; pass `anonymous`.
 */
export class EventsApi {
  constructor(private readonly t: Transport) {}

  /** The shows to come, soonest first, each once however many calendars list it. */
  async list(ctx: ApiContext, query: PublicEventQuery = {}): Promise<Result<PublicEventList, never>> {
    const r = await this.t.get(ctx, "/events", {
      query: { ...query, from: query.from?.toISOString(), to: query.to?.toISOString() },
    });
    return forward(
      r,
      (j) => parse(PublicEventList, j),
      (e) => {
        throw new UnexpectedApiError(e);
      },
    );
  }

  /** The codes of the US states with a show to come, sorted. `hosted` narrows them as it narrows the list. */
  async states(ctx: ApiContext, query: { hosted?: boolean } = {}): Promise<Result<string[], never>> {
    const r = await this.t.get(ctx, "/events/states", { query });
    return forward(
      r,
      (j) => parse(StatesEnvelope, j).states,
      (e) => {
        throw new UnexpectedApiError(e);
      },
    );
  }

  /** A hosted show's kinds of table, each with how many are left. Unauthenticated. */
  async tables(ctx: ApiContext, eventId: string): Promise<Result<PublicTableShow, EventNotFound>> {
    if (!EVENT_ID.test(eventId)) return fail(new EventNotFound(eventId));
    const r = await this.t.get(ctx, `/events/${eventId}/tables`);
    return forward(
      r,
      (j) => parse(PublicTableShow, j),
      (e) => {
        if (e instanceof ValidationError && e.code === ErrorCode.DoesntExist) return new EventNotFound(eventId, e);
        throw new UnexpectedApiError(e);
      },
    );
  }

  /**
   * Reserves a table for an email, held for five minutes with its payment (null for a
   * table that costs nothing, reserved at once). Needs a session, a guest one included:
   * it owns the hold. One table per email per show.
   */
  async reserveTable(
    ctx: ApiContext,
    eventId: string,
    body: { kindId: string; email: string },
  ): Promise<Result<ReservedTable, ApiNotAuthorized | EventNotFound | TableKindNotFound | TableClaimRefused>> {
    if (!EVENT_ID.test(eventId)) return fail(new EventNotFound(eventId));
    if (!body.kindId) return fail(new TableKindNotFound(body.kindId));
    const r = await this.t.post(ctx, `/events/${eventId}/tables/reserve`, { body });
    return forward(
      r,
      (j) => parse(ReservedTable, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.DoesntExist) {
            if (e.incorrectValue === "eventId") return new EventNotFound(eventId, e);
            if (e.incorrectValue === "kindId") return new TableKindNotFound(body.kindId, e);
          }
          if (["eventId", "kindId", "email"].includes(e.incorrectValue ?? "")) {
            return new TableClaimRefused(e.detailedDesc, e);
          }
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** The session's reservation: PAID, with its code, once Stripe's webhook settles it. */
  async reservation(
    ctx: ApiContext,
    orderId: string,
  ): Promise<Result<TableReservation, ApiNotAuthorized | TableOrderNotFound>> {
    if (!orderId) return fail(new TableOrderNotFound(orderId));
    const r = await this.t.get(ctx, `/table-reservations/${orderId}`);
    return forward(
      r,
      (j) => parse(TableReservation, j),
      (e) => {
        if (e instanceof ValidationError && e.incorrectValue === "orderId") return new TableOrderNotFound(orderId, e);
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** Gives up a reserved table before paying: its payment is voided and the table goes back on sale. */
  async cancelReservation(
    ctx: ApiContext,
    orderId: string,
  ): Promise<Result<void, ApiNotAuthorized | TableOrderNotFound | TableClaimRefused>> {
    if (!orderId) return fail(new TableOrderNotFound(orderId));
    const r = await this.t.post(ctx, `/table-reservations/${orderId}/cancel`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError && e.incorrectValue === "orderId") {
          return e.code === ErrorCode.DoesntExist
            ? new TableOrderNotFound(orderId, e)
            : new TableClaimRefused(e.detailedDesc, e);
        }
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /**
   * Subscribes a calendar app to the shows in some US states (code or name). No session
   * needed; the subscription's id is its feed's secret, so keep it to change or revoke it.
   */
  async createCalendarSubscription(
    ctx: ApiContext,
    states: string[],
  ): Promise<Result<ShowCalendarSubscription, CalendarStatesRefused>> {
    const r = await this.t.post(ctx, "/events/calendar-subscriptions", { body: { states } });
    return forward(
      r,
      (j) => parse(ShowCalendarSubscription, j),
      (e) => {
        if (e instanceof ValidationError && e.incorrectValue === "states") {
          return new CalendarStatesRefused(e.detailedDesc, e);
        }
        throw new UnexpectedApiError(e);
      },
    );
  }

  async calendarSubscription(
    ctx: ApiContext,
    id: string,
  ): Promise<Result<ShowCalendarSubscription, CalendarSubscriptionNotFound>> {
    if (!EVENT_ID.test(id)) return fail(new CalendarSubscriptionNotFound(id));
    const r = await this.t.get(ctx, `/events/calendar-subscriptions/${id}`);
    return forward(
      r,
      (j) => parse(ShowCalendarSubscription, j),
      (e) => {
        if (e instanceof ValidationError && e.incorrectValue === "id") return new CalendarSubscriptionNotFound(id, e);
        throw new UnexpectedApiError(e);
      },
    );
  }

  /** Points a subscription at other states, reactivating it if revoked. Its feed URL stays the same. */
  async updateCalendarSubscription(
    ctx: ApiContext,
    id: string,
    states: string[],
  ): Promise<Result<ShowCalendarSubscription, CalendarSubscriptionNotFound | CalendarStatesRefused>> {
    if (!EVENT_ID.test(id)) return fail(new CalendarSubscriptionNotFound(id));
    const r = await this.t.put(ctx, `/events/calendar-subscriptions/${id}`, { body: { states } });
    return forward(
      r,
      (j) => parse(ShowCalendarSubscription, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "id") return new CalendarSubscriptionNotFound(id, e);
          if (e.incorrectValue === "states") return new CalendarStatesRefused(e.detailedDesc, e);
        }
        throw new UnexpectedApiError(e);
      },
    );
  }

  /** Revokes a subscription: its feed answers empty from now on, which removes its shows from the calendar. */
  async revokeCalendarSubscription(
    ctx: ApiContext,
    id: string,
  ): Promise<Result<ShowCalendarSubscription, CalendarSubscriptionNotFound>> {
    if (!EVENT_ID.test(id)) return fail(new CalendarSubscriptionNotFound(id));
    const r = await this.t.delete(ctx, `/events/calendar-subscriptions/${id}`);
    return forward(
      r,
      (j) => parse(ShowCalendarSubscription, j),
      (e) => {
        if (e instanceof ValidationError && e.incorrectValue === "id") return new CalendarSubscriptionNotFound(id, e);
        throw new UnexpectedApiError(e);
      },
    );
  }

  /** One show with its description. A show that is over still opens. */
  async get(ctx: ApiContext, eventId: string): Promise<Result<PublicEventDetail, EventNotFound>> {
    // Not an id, so no event: sent as it is, the path could reach the list, or the states in any case of letters.
    if (!EVENT_ID.test(eventId) || eventId.toLowerCase() === "states") return fail(new EventNotFound(eventId));
    const r = await this.t.get(ctx, `/events/${eventId}`);
    return forward(
      r,
      (j) => parse(PublicEventDetail, j),
      (e) => {
        if (e instanceof ValidationError && e.code === ErrorCode.DoesntExist) return new EventNotFound(eventId, e);
        throw new UnexpectedApiError(e);
      },
    );
  }
}
